import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PenTool,
  Music2,
  MessageSquare,
  LayoutDashboard,
  Bot,
  LogOut,
  Sparkles,
  Image as ImageIcon,
  Calendar as CalendarIcon,
  FolderSearch,
  Plug,
  Coins,
  Workflow,
  ShieldCheck,
  Radar,
  Rocket,
  Megaphone,
} from "lucide-react";
import SEO from "@/components/SEO";
import { Wordmark } from "@/components/Brand";
import StreakBadge from "@/components/StreakBadge";
import ThemeToggle from "@/components/ThemeToggle";
import TrialBanner from "@/components/TrialBanner";
import TrialNudgeModal from "@/components/TrialNudgeModal";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

import EmpireBuilder from "@/components/dashboard/EmpireBuilder";
import WingmanPanel from "@/components/dashboard/WingmanPanel";
import WriteOncePanel from "@/components/dashboard/WriteOncePanel";
import TikTokPost from "@/components/dashboard/TikTokPost";
import ImageStudio from "@/components/dashboard/ImageStudio";
import CalendarPanel from "@/components/dashboard/CalendarPanel";
import Vault from "@/components/dashboard/Vault";
import Connections from "@/components/dashboard/Connections";
import LocalSignals from "@/components/dashboard/LocalSignals";
import AutoPilot from "@/components/dashboard/AutoPilot";
import PromoPack from "@/components/dashboard/PromoPack";
import MissionBrief, { ConnectChannelsModal } from "@/components/dashboard/MissionBrief";
import ChannelHealth from "@/components/dashboard/ChannelHealth";
import FloatingWingman from "@/components/dashboard/FloatingWingman";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "writeonce", label: "Write Once", icon: PenTool },
  { id: "empire", label: "Empire Builder", icon: Workflow },
  { id: "wingman", label: "Tam", icon: Sparkles },
  { id: "tiktok", label: "TikTok Post", icon: Music2 },
  { id: "autopilot", label: "Auto-Pilot", icon: Rocket },
  { id: "image", label: "Image Studio", icon: ImageIcon },
  { id: "signals", label: "Local Signals", icon: Radar },
  { id: "calendar", label: "Calendar", icon: CalendarIcon },
  { id: "vault", label: "The Vault", icon: FolderSearch },
  { id: "connections", label: "Connections", icon: Plug },
];

const Dashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch { /* non-fatal */ }
  };

  // First-visit: show Connect Channels modal if onboarding step 1 isn't done
  // and the user hasn't dismissed it before.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/state");
        if (cancelled) return;
        const step1 = data.steps?.find((s) => s.id === "connect_channels");
        if (step1 && !step1.done && !data.dismissed_connect_modal) {
          setShowConnectModal(true);
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    (async () => { await loadStats(); })();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const cards = [
    { id: "content", label: "Content Posts", value: stats?.content_posts ?? 0, icon: PenTool, target: "writeonce" },
    { id: "dms", label: "DM Replies", value: stats?.dm_replies ?? 0, icon: MessageSquare, target: "empire" },
    { id: "images", label: "Images", value: stats?.images ?? 0, icon: ImageIcon, target: "image" },
    { id: "scheduled", label: "Scheduled", value: stats?.scheduled_events ?? 0, icon: CalendarIcon, target: "calendar" },
    { id: "wingman", label: "Tam Msgs", value: stats?.wingman_messages ?? 0, icon: Sparkles, target: "wingman" },
    { id: "tasks", label: "Assistant Tasks", value: stats?.assistant_tasks ?? 0, icon: Bot, target: "wingman" },
  ];

  return (
    <div className="min-h-screen bg-[#050B14] text-white" data-testid="dashboard-page">
      <SEO title="Dashboard" description="ClydeFlow control room." path="/dashboard" noindex />
      <TrialNudgeModal trial={user?.trial} />
      {showConnectModal && (
        <ConnectChannelsModal
          onClose={() => setShowConnectModal(false)}
          onConnectChannels={() => {
            setShowConnectModal(false);
            setTab("connections");
          }}
        />
      )}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050B14]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="dashboard-brand"><Wordmark className="h-12" /></Link>
          <div className="flex items-center gap-4">
            <StreakBadge streak={user?.streak} compact testIdPrefix="dashboard-streak" />
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="dashboard-admin-link"
                className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A192F] border border-cyan-400/40 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.2em] rounded-sm hover:bg-[#00F0FF] hover:text-[#050B14] hover:border-[#00F0FF] transition-all"
                title="Admin Command Center"
              >
                <ShieldCheck size={12} /> Command Center
              </Link>
            )}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-[#0A192F] border border-white/10 rounded-sm">
              <Coins size={14} className="text-cyan-300" />
              <span className="font-mono text-xs text-white" data-testid="dashboard-token-balance">
                {user?.role === "admin" ? "∞" : (user?.wingman_tokens ?? 0)}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">
                {user?.role === "admin" ? "unlimited" : "tokens"}
              </span>
            </div>
            <span
              className="px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] bg-cyan-400/10 text-cyan-300 border border-cyan-400/20"
              data-testid="dashboard-plan-badge"
            >
              {stats?.plan || user?.plan || "free"}
            </span>
            <ThemeToggle />
            <div className="hidden md:flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" />
              )}
              <span className="text-sm text-slate-300" data-testid="dashboard-user-name">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout-btn"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
        {/* Tab strip */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-1 overflow-x-auto" data-testid="dashboard-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={[
                  "px-4 py-3 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition-colors",
                  active
                    ? "border-cyan-400 text-white"
                    : "border-transparent text-slate-400 hover:text-white",
                ].join(" ")}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {tab === "overview" && (
          <div data-testid="overview-panel">
            <TrialBanner
              trial={user?.trial}
              onUpgrade={() => navigate("/#pricing") || window.location.assign("/#pricing")}
            />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3 mt-8">
              The flow today
            </p>
            <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-white">
              Hey {user?.name?.split(" ")[0] || "founder"} &mdash; let&apos;s ship.
            </h1>

            {/* Streak strip */}
            {user?.streak && (
              <div
                data-testid="overview-streak-card"
                className="mt-8 p-5 bg-gradient-to-r from-orange-500/10 via-[#0A192F] to-[#0A192F] border border-orange-400/30 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4">
                  <StreakBadge streak={user.streak} testIdPrefix="overview-streak" />
                  <div className="text-sm">
                    <p className="text-white font-heading font-bold">
                      {user.streak.active_today
                        ? "You've checked in today. Keep it lit."
                        : user.streak.current > 0
                          ? "Don't break the chain — send Tam a message today."
                          : "Start your streak — send Tam your first message."}
                    </p>
                    {user.streak.next_milestone && (
                      <p className="mt-1 text-xs font-mono text-orange-200 uppercase tracking-widest">
                        Next bonus · +{user.streak.next_milestone_bonus} tokens at {user.streak.next_milestone} days
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setTab("wingman")}
                  data-testid="overview-streak-cta"
                  className="px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF]"
                >
                  Open Tam
                </button>
              </div>
            )}

            <div className="mt-8">
              <MissionBrief
                onJumpTab={(t) => setTab(t)}
                onChange={() => { loadStats(); checkAuth && checkAuth(); }}
              />
            </div>

            <div className="mt-8">
              <ChannelHealth onJump={(t) => setTab(t)} />
            </div>

            <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4">
              {cards.map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    onClick={() => c.target && setTab(c.target)}
                    type="button"
                    className="text-left p-6 bg-[#0A192F] border border-white/10 hover:border-cyan-400/40 hover:bg-[#0A192F]/80 rounded-sm transition-colors group focus:outline-none focus:border-cyan-400"
                    data-testid={`stat-card-${c.id}`}
                  >
                    <Icon size={18} className="text-cyan-300" />
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 group-hover:text-slate-400">
                      {c.label}
                    </p>
                    <p className="mt-2 font-heading text-3xl font-black text-white">{c.value}</p>
                    <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-400/60 group-hover:text-cyan-300">
                      Open →
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-4">
              {[
                {id: "empire", t: "Empire Builder", d: "Set it once, it posts for you.", icon: Workflow},
                {id: "wingman", t: "Ask Tam", d: "Your productivity agent.", icon: Sparkles},
                {id: "image", t: "Open Image Studio", d: "GPT Image 1 + Nano Banana.", icon: ImageIcon},
              ].map((q) => {
                const Icon = q.icon;
                return (
                  <button
                    key={q.id}
                    onClick={() => setTab(q.id)}
                    data-testid={`overview-quick-${q.id}`}
                    className="text-left p-6 bg-gradient-to-br from-[#0A192F] to-[#050B14] border border-white/10 hover:border-cyan-400/40 rounded-sm transition-all"
                  >
                    <Icon size={18} className="text-cyan-300" />
                    <p className="mt-3 font-heading text-xl font-bold text-white">{q.t}</p>
                    <p className="mt-1 text-slate-400 text-sm">{q.d}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "writeonce" && <WriteOncePanel />}
        {tab === "empire" && <EmpireBuilder />}
        {tab === "wingman" && (
          <WingmanPanel user={user} refreshUser={checkAuth} />
        )}
        {tab === "tiktok" && <TikTokPost />}
        {tab === "autopilot" && <AutoPilot />}
        {tab === "promo" && <PromoPack />}
        {tab === "image" && <ImageStudio />}
        {tab === "signals" && <LocalSignals />}
        {tab === "calendar" && <CalendarPanel />}
        {tab === "vault" && <Vault />}
        {tab === "connections" && <Connections />}
      </main>

      {/* Wingman, freed from his box — floats bottom-right everywhere except his own tab */}
      {tab !== "wingman" && (
        <FloatingWingman
          user={user}
          refreshUser={checkAuth}
          onOpenFull={() => setTab("wingman")}
          onNavigate={(t) => setTab(t)}
          pageLabel={(TABS.find((x) => x.id === tab) || {}).label || "Dashboard"}
        />
      )}
    </div>
  );
};

export default Dashboard;
